import { CurrencyUtils } from '@brunommsilva/react-foundations';
import { useToasts } from '@brunommsilva/react-foundations';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { TEST_IDS } from '../../config/test-ids';
import useDeleteAccount from '../../hooks/accounts/use-delete-account';
import { Account } from '../../models/accounts';

export interface Props {
  show: boolean;
  account?: Account;
  onDeleted: () => void;
  onClose: () => void;
}

function DeleteAccountModal({ show, onDeleted, onClose, account }: Props) {
  const { t } = useTranslation();
  const { showToast } = useToasts();
  const {
    deleteAccount,
    isDeleting,
    wasDeletedWithSuccess,
    wasDeletedWithoutSuccess,
  } = useDeleteAccount();

  const handleDeleteAccount = () => {
    if (account) {
      deleteAccount(account.id);
    }
  };
  const handleClose = () => onClose();

  useEffect(() => {
    if (wasDeletedWithSuccess) {
      showToast({
        title: t('DELETE_ACCOUNT_SUCCESS_TOAST.TITLE'),
        message: t('DELETE_ACCOUNT_SUCCESS_TOAST.MESSAGE'),
      });
      onDeleted();
    }
    if (wasDeletedWithoutSuccess) {
      showToast({
        title: t('DELETE_ACCOUNT_ERROR_TOAST.TITLE'),
        message: t('DELETE_ACCOUNT_ERROR_TOAST.MESSAGE'),
        type: 'error',
      });
    }
  }, [wasDeletedWithSuccess, wasDeletedWithoutSuccess]);

  const renderAccountNotZeroedModal = () => {
    return (
      <Dialog open={show} onClose={handleClose}>
        <DialogTitle>{t('DELETE_ACCOUNT_MODAL.TITLE_NOT_ZEROED')}</DialogTitle>
        <DialogContent>
          {t('DELETE_ACCOUNT_MODAL.DESCRIPTION_NOT_ZEROED', {
            name: account?.name,
            balance: CurrencyUtils.formatWithCurrency(account?.balance || 0, 2),
          })}
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={handleClose}>
            {t('DELETE_ACCOUNT_MODAL.CLOSE')}
          </Button>
          <Button disabled color="error" onClick={handleClose}>
            {t('DELETE_ACCOUNT_MODAL.CONFIRM')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderDeleteConfirmation = () => {
    return (
      <Dialog open={show} onClose={handleClose}>
        <DialogTitle>{t('DELETE_ACCOUNT_MODAL.TITLE')}</DialogTitle>
        <DialogContent>
          {t('DELETE_ACCOUNT_MODAL.DESCRIPTION', { name: account?.name })}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            disabled={isDeleting}
            color="primary"
            onClick={handleClose}
          >
            {t('DELETE_ACCOUNT_MODAL.CLOSE')}
          </Button>
          <Button
            variant="contained"
            data-testid={TEST_IDS.accounts.deleteConfirm}
            disabled={isDeleting}
            color="error"
            onClick={handleDeleteAccount}
          >
            {t('DELETE_ACCOUNT_MODAL.CONFIRM')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (account?.balance === 0) {
    return renderDeleteConfirmation();
  }

  return renderAccountNotZeroedModal();
}

export default DeleteAccountModal;
